import { cn } from "@tcg/ui";
import { Minus, Pencil, Percent, Plus, Trash2, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText, useYen } from "@/components/MoneyText";
import type { CartLine as Line } from "@/lib/types";

import { ItemBadges } from "./ItemBadges";
import { clockTime, localized } from "./labels";

export interface CartLineProps {
  line: Line;
  soldElsewhere: boolean;
  /** When it was sold elsewhere (ISO) — 「10:02に売れました」 (T07-40). */
  soldAt?: string | null;
  disabled: boolean;
  onEditPrice: (line: Line) => void;
  onDiscount: (line: Line) => void;
  onRemove: (line: Line) => void;
  onQty: (line: Line, qty: number) => void;
}

/**
 * One basket line (design/05 §3.1): qty × name, Rank / grade / 未開封 / 委託, the item code / cert,
 * the price the server computed (after discounts), ✎ price, 値引, 🗑; a qty stepper for sealed
 * items only (one-of-a-kind cards are always 1). A card sold elsewhere turns red with the reason.
 */
export function CartLine({
  line,
  soldElsewhere,
  soldAt = null,
  disabled,
  onEditPrice,
  onDiscount,
  onRemove,
  onQty,
}: CartLineProps) {
  const { t, i18n } = useTranslation();
  const yen = useYen();
  const locale = i18n.resolvedLanguage ?? "ja";
  const warnings = line.warnings.filter((w) => w.code !== "sold_elsewhere");
  const problem = soldElsewhere || !line.canSell;
  const code = line.grading?.certNumber
    ? t("cart.cert", { cert: line.grading.certNumber })
    : line.sku
      ? t("cart.code", { code: line.sku })
      : null;
  return (
    <li
      data-line-id={line.id}
      data-state={soldElsewhere ? "sold-elsewhere" : line.canSell ? "ok" : "problem"}
      className={cn("flex flex-col gap-1 border-b px-3 py-2", problem && "bg-status-problem/10")}
    >
      <div className="flex items-start gap-2">
        <span className="min-w-8 pt-0.5 text-lg font-semibold tabular-nums">{line.qty}×</span>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-base font-medium">{localized(line.title, locale)}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <ItemBadges
              productType={line.productType}
              rawRank={line.rawRank}
              grading={line.grading}
              consigned={line.consignment.consigned}
              consignedRef={line.consignment.consignorRef ?? null}
              size="sm"
            />
            {code ? <span className="font-mono">{code}</span> : null}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <MoneyText amount={line.lineTotal} size="lg" />
          {line.discountAmount > 0 ? (
            <span className="text-xs text-pos-problem tabular-nums">
              {t("cart.line_discount", { amount: yen(line.discountAmount) })}
            </span>
          ) : null}
          {line.overridePrice !== null && line.overrideReason ? (
            <span className="text-xs text-muted-foreground">
              {t("cart.override_note", { reason: line.overrideReason })}
            </span>
          ) : null}
        </div>
      </div>
      {soldElsewhere ? (
        <p role="alert" className="flex items-center gap-1 text-sm font-semibold text-pos-problem">
          <TriangleAlert aria-hidden className="size-4" />
          <span>{t("sale.sold_elsewhere_line")}</span>
          {soldAt ? (
            <span data-testid="sold-at">
              · {t("tile.sold_live", { when: clockTime(soldAt, locale) })}
            </span>
          ) : null}
        </p>
      ) : null}
      {warnings.map((w) => (
        <p key={w.code} className="flex items-center gap-1 text-sm text-pos-problem">
          <TriangleAlert aria-hidden className="size-4" />
          {t(`warning.${w.code}`, { ...w.details, defaultValue: t("blocker.precheck_failed") })}
        </p>
      ))}
      <div className="flex items-center justify-end gap-1">
        {line.kind === "sealed" ? (
          <div className="mr-auto flex items-center gap-1" role="group" aria-label={t("cart.qty")}>
            <IconButton
              label={t("cart.qty_minus")}
              disabled={disabled || line.qty <= 1}
              onClick={() => onQty(line, line.qty - 1)}
            >
              <Minus aria-hidden />
            </IconButton>
            <span className="min-w-8 text-center text-lg tabular-nums">{line.qty}</span>
            <IconButton
              label={t("cart.qty_plus")}
              disabled={disabled}
              onClick={() => onQty(line, line.qty + 1)}
            >
              <Plus aria-hidden />
            </IconButton>
          </div>
        ) : null}
        <IconButton
          label={t("cart.edit_price")}
          disabled={disabled || soldElsewhere}
          onClick={() => onEditPrice(line)}
        >
          <Pencil aria-hidden />
        </IconButton>
        <IconButton
          label={t("cart.discount")}
          disabled={
            disabled ||
            soldElsewhere ||
            (line.consignment.consigned && !line.consignment.discountAllowed)
          }
          onClick={() => onDiscount(line)}
        >
          <Percent aria-hidden />
        </IconButton>
        <IconButton
          label={t("cart.remove")}
          disabled={disabled}
          onClick={() => onRemove(line)}
          danger={soldElsewhere}
        >
          <Trash2 aria-hidden />
        </IconButton>
      </div>
    </li>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex size-pos-touch items-center justify-center rounded-md border [&_svg]:size-5 hover:bg-accent disabled:opacity-40",
        danger && "border-status-problem bg-status-problem text-white",
      )}
    >
      {children}
    </button>
  );
}
